import React from 'react';
import type {IFormFieldV2, IFormSettings} from 'src/types/FormV2';

export interface FormBuilderState {
  fieldList: IFormFieldV2[];
  selectedFieldId: string | null; // Primary field selection method
  formSettings: IFormSettings;
  isFieldSettingsValid: boolean;
  settingsSeed: number;
}

export const initialFormSettings: IFormSettings = {
  identifier: '',
  active: true,
  name: 'New Form Name',
  title: 'New Form Title',
  subformOnly: false,
  extensionOnly: false,
  triggerAnalyticSafetyEvent: false,
  failable: false,
  accessAllowEditAfterPublish: false,
  accessAllowSupervisors: false,
  accessAllowUserGroups: [],
  metadata: {
    anonymousSubmission: false,
    description: undefined,
    enableFormIdentifierField: true,
  },
  mode: 'create',
  lockIdentifier: false,
  projectId: null,
  categoryId: null,
  sensitiveData: false,
};

type FormBuilderAction =
  | {type: 'updateFieldList'; payload: IFormFieldV2[]}
  | {type: 'updateFormSettings'; payload: IFormSettings}
  | {type: 'loadFormSettings'; payload: IFormSettings}
  | {type: 'updateSelectedFieldId'; payload: string | null}
  | {type: 'setFieldSettingsValid'; payload: boolean}
  | {type: 'resetFormBuilderState'};

const initialState: FormBuilderState = {
  fieldList: [],
  selectedFieldId: null,
  formSettings: initialFormSettings,
  isFieldSettingsValid: true,
  settingsSeed: 0,
};

const formBuilderReducer = (
  state: FormBuilderState,
  action: FormBuilderAction
): FormBuilderState => {
  switch (action.type) {
    case 'updateFieldList': {
      const fieldList = action.payload;

      // Auto-update selectedFieldId if current selection is invalid
      let selectedFieldId = state.selectedFieldId;
      if (state.selectedFieldId) {
        const fieldExists = fieldList.some(
          field => field.invariantId === state.selectedFieldId
        );
        if (!fieldExists) {
          selectedFieldId = fieldList.length > 0 ? fieldList[0].invariantId! : null;
        }
      } else if (fieldList.length > 0) {
        selectedFieldId = fieldList[0].invariantId!;
      }

      return {
        ...state,
        fieldList,
        selectedFieldId,
        isFieldSettingsValid:
          selectedFieldId === null ? true : state.isFieldSettingsValid,
      };
    }
    case 'updateFormSettings':
      return {...state, formSettings: action.payload};
    case 'loadFormSettings':
      return {
        ...state,
        formSettings: action.payload,
        settingsSeed: state.settingsSeed + 1,
      };
    case 'updateSelectedFieldId':
      return {
        ...state,
        selectedFieldId: action.payload,
        isFieldSettingsValid:
          action.payload === null ? true : state.isFieldSettingsValid,
      };
    case 'setFieldSettingsValid':
      return {...state, isFieldSettingsValid: action.payload};
    case 'resetFormBuilderState':
      return initialState;
    default:
      return state;
  }
};

export interface FormBuilderContextValue {
  state: FormBuilderState;
  dispatch: React.Dispatch<FormBuilderAction>;
}

const FormBuilderContext = React.createContext<FormBuilderContextValue | null>(
  null
);

export interface FormBuilderProviderProps {
  initialFieldList?: IFormFieldV2[];
  initialFormSettings?: IFormSettings;
  children: React.ReactNode;
}

export const FormBuilderProvider: React.FC<FormBuilderProviderProps> = ({
  initialFieldList = [],
  initialFormSettings: initialSettings = initialFormSettings,
  children,
}) => {
  const [state, dispatch] = React.useReducer(
    formBuilderReducer,
    {fieldList: initialFieldList, formSettings: initialSettings},
    ({fieldList, formSettings}) => ({
      fieldList,
      selectedFieldId: fieldList.length > 0 ? fieldList[0].invariantId! : null,
      formSettings,
      isFieldSettingsValid: true,
      settingsSeed: 0,
    })
  );

  const value = React.useMemo(() => ({state, dispatch}), [state]);

  return (
    <FormBuilderContext.Provider value={value}>
      {children}
    </FormBuilderContext.Provider>
  );
};

export const useFormBuilderContext = (): FormBuilderContextValue => {
  const context = React.useContext(FormBuilderContext);
  if (!context) {
    throw new Error(
      'useFormBuilderContext must be used within a FormBuilderProvider'
    );
  }
  return context;
};
